import React, { useState, useEffect, useRef } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Alert,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  InputAdornment,
  Popover,
  List,
  ListItemButton,
  ListItemText
} from '@mui/material';
import {
  Person,
  Edit,
  Save,
  Cancel,
  Verified,
  Warning,
  ArrowBack,
  ContentCopy,
  Share,
  WhatsApp,
  ArrowDropDown,
  Search
} from '@mui/icons-material';
import merchantAPI, { merchantAuthAPI } from '../../../services/merchantAPI';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import { useNavigate } from 'react-router-dom';
import { masterDataConfigAPI } from '../../../services/api';
import FlutterServicesAPI from '../../../services/flutterServices';
import { AutoCloseAlert } from '../../Common';
import { referralCodeAPI } from '../../../services/merchantAPI';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import useRouteMeta from '../../../hooks/useRouteMeta';
import { getRegisterUrl } from '../../../utils/environmentUtils';

const ProfilePage = () => {
  const navigate = useNavigate();
  useRouteMeta();
  const profileInfoRef = useRef(null);
  const [editMode, setEditMode] = useState(false);
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [emailVerification, setEmailVerification] = useState({ otpSent: false, otp: '', verified: false, token: '', loading: false });
  const [mobileVerification, setMobileVerification] = useState({ otpSent: false, otp: '', verified: false, token: '', loading: false });
  const originalContactRef = useRef({ email: '', whatsapp_number: '', whatsapp_code: '' });

  const [emailOtpOpen, setEmailOtpOpen] = useState(false);
  const [mobileOtpOpen, setMobileOtpOpen] = useState(false);
  const [emailOtpArray, setEmailOtpArray] = useState(['', '', '', '']);
  const [mobileOtpArray, setMobileOtpArray] = useState(['', '', '', '']);
  const emailOtpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const mobileOtpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [countryCodes, setCountryCodes] = useState([]);
  const [countryAnchorEl, setCountryAnchorEl] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');
  const searchInputRef = React.useRef(null);
  const selectedCountryRef = React.useRef(null);



  const [referralCode, setReferralCode] = useState(null);
  const [referralCodeLoading, setReferralCodeLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const [shareDialog, setShareDialog] = useState(false);

  const { merchant, refreshProfile } = useMerchantAuth();
  const { hasPermission } = useMerchantPermissions();
  const canEditProfile = hasPermission('Merchant Profile', 'edit');
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = React.useState(true);

  React.useEffect(() => {
    const checkAuthVisibility = async () => {
      try {
        const response = await merchantAuthAPI.checkAuthVisibility();
        if (response.data && typeof response.data.show_whatsapp_sms_auth === 'boolean') {
          setShowWhatsAppSmsAuth(response.data.show_whatsapp_sms_auth);
        }
      } catch (err) {
        console.error('❌ ProfilePage: Failed to check auth visibility:', err);
      }
    };
    checkAuthVisibility();
  }, []);

  // Validation helpers using libphonenumber-js
  const validatePhone = (phone, countryCode = '+91') => {
    if (!phone) return false;
    try {
      const phoneValue = phone.trim();
      const countryCodeWithoutPlus = countryCode.replace('+', '');

      let phoneNumber = null;

      // Try parsing as international if it starts with +
      if (phoneValue.startsWith('+')) {
        phoneNumber = parsePhoneNumberFromString(phoneValue);
      }

      // If no result, prepend country code and try as international
      if (!phoneNumber) {
        const withCountryCode = countryCode + phoneValue;
        phoneNumber = parsePhoneNumberFromString(withCountryCode);
      }

      return phoneNumber && phoneNumber.isValid();
    } catch (error) {
      return false;
    }
  };

  const validateWhatsApp = (whatsapp, countryCode = '+91') => validatePhone(whatsapp, countryCode);
  const validateGST = (gst) => {
    if (!gst) return false;
    const cleanGST = gst.trim().toUpperCase();
    if (cleanGST.length !== 15) return false;
    const pattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!pattern.test(cleanGST)) return false;
    const stateCode = cleanGST.substring(0, 2);
    const validStateCodes = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36', '37'];
    if (!validStateCodes.includes(stateCode)) return false;
    const panPart = cleanGST.substring(2, 12);
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(panPart)) return false;
    if (!/^[0-9A-Z]$/.test(cleanGST.charAt(12))) return false;
    if (cleanGST.charAt(13) !== 'Z') return false;
    if (!/^[0-9A-Z]$/.test(cleanGST.charAt(14))) return false;
    if (new Set(panPart.substring(0, 5)).size === 1) return false;
    return true;
  };
  const validatePincode = (pincode) => /^[1-9][0-9]{5}$/.test(pincode);

  const validateFields = (profile) => {
    const errors = {};
    if (!profile.name?.trim()) {
      errors.name = 'Full name is required.';
    } else if (!/^[A-Za-z\s]+$/.test(profile.name)) {
      errors.name = 'Full name can only contain alphabets and spaces.';
    }

    // During app review, mobile/whatsapp is optional — only validate format if a value is present
    if (showWhatsAppSmsAuth) {
      if (!profile.whatsapp_number?.trim()) { errors.whatsapp_number = 'WhatsApp number is required.'; }
      else if (!validateWhatsApp(profile.whatsapp_number, profile.whatsapp_code)) { errors.whatsapp_number = 'Please enter a valid whatsapp number.'; }
    } else if (profile.whatsapp_number?.trim()) {
      if (!validateWhatsApp(profile.whatsapp_number, profile.whatsapp_code)) { errors.whatsapp_number = 'Please enter a valid whatsapp number.'; }
    }

    if (!profile.company_name?.trim()) errors.company_name = 'Company name is required.';

    // Format validation for optional fields
    if (profile.gst_number && profile.gst_number.trim() && !validateGST(profile.gst_number)) {
      errors.gst_number = 'Enter a valid GST number';
    }
    if (profile.pincode && profile.pincode.trim() && !validatePincode(profile.pincode)) {
      errors.pincode = 'Enter a valid 6-digit pincode.';
    }
    return errors;
  };

  const handleOtpChange = (type, index, value) => {
    const cleanValue = value.replace(/\D/g, '');
    if (!cleanValue && value !== '') return;

    const isEmail = type === 'email';
    const otpArray = isEmail ? emailOtpArray : mobileOtpArray;
    const setOtpArray = isEmail ? setEmailOtpArray : setMobileOtpArray;
    const refs = isEmail ? emailOtpRefs : mobileOtpRefs;

    if (cleanValue.length > 1) {
      const pasted = cleanValue.slice(0, 4);
      const newOtp = [...otpArray];
      pasted.split('').forEach((ch, i) => {
        if (i < 4) newOtp[i] = ch;
      });
      setOtpArray(newOtp);
      const targetIndex = Math.min(pasted.length, 3);
      refs[targetIndex].current?.focus();
      return;
    }

    const newOtp = [...otpArray];
    newOtp[index] = cleanValue.slice(-1);
    setOtpArray(newOtp);

    if (cleanValue !== '' && index < 3) {
      refs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (type, index, e) => {
    const isEmail = type === 'email';
    const otpArray = isEmail ? emailOtpArray : mobileOtpArray;
    const refs = isEmail ? emailOtpRefs : mobileOtpRefs;
    if (e.key === 'Backspace' && !otpArray[index] && index > 0) {
      refs[index - 1].current?.focus();
    }
  };

  const handlePaste = (type, e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (!pasted) return;
    const isEmail = type === 'email';
    const otpArray = isEmail ? emailOtpArray : mobileOtpArray;
    const setOtpArray = isEmail ? setEmailOtpArray : setMobileOtpArray;
    const refs = isEmail ? emailOtpRefs : mobileOtpRefs;
    const newOtp = [...otpArray];
    pasted.split('').forEach((ch, i) => { newOtp[i] = ch; });
    setOtpArray(newOtp);
    refs[Math.min(pasted.length, 3)].current?.focus();
  };

  useEffect(() => {
    loadProfile();
    loadCountries();
    loadCountryCodes();
    loadReferralCode();
  }, []);



  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await merchantAPI.getProfile();
      setProfile(response.data.merchant);
      originalContactRef.current = {
        email: (response.data.merchant.email || '').trim().toLowerCase(),
        whatsapp_number: (response.data.merchant.whatsapp_number || '').trim(),
        whatsapp_code: (response.data.merchant.whatsapp_code || '').trim()
      };
      setEmailVerification({ otpSent: false, otp: '', verified: true, token: '', loading: false });
      setMobileVerification({ otpSent: false, otp: '', verified: true, token: '', loading: false });
      if (response.data.merchant.country_id) loadStates(response.data.merchant.country_id);
      if (response.data.merchant.state_id) loadCities(response.data.merchant.state_id);
    } catch (error) {
      console.error('Failed to load profile:', error);
      setError('Failed to load profile information');
    } finally {
      setLoading(false);
    }
  };

  const loadCountries = async () => {
    try {
      const response = await masterDataConfigAPI.getCountries();
      setCountries(response.data.data || []);
    } catch (error) { console.error('Failed to load countries:', error); }
  };

  const loadCountryCodes = async () => {
    try {
      const response = await masterDataConfigAPI.getCountries();
      setCountryCodes(response.data.data || []);
    } catch (error) { console.error('Failed to load country codes:', error); }
  };

  const loadStates = async (countryId) => {
    if (!countryId) return;
    setLoadingStates(true);
    try {
      const response = await masterDataConfigAPI.getStatesByCountry(countryId);
      setStates(response.data.data || []);
    } catch (error) { console.error('Failed to load states:', error); } finally { setLoadingStates(false); }
  };

  const loadCities = async (stateId) => {
    if (!stateId) return;
    setLoadingCities(true);
    try {
      const response = await masterDataConfigAPI.getCitiesByState(stateId);
      setCities(response.data.data || []);
    } catch (error) { console.error('Failed to load cities:', error); } finally { setLoadingCities(false); }
  };

  const loadReferralCode = async () => {
    setReferralCodeLoading(true);
    try {
      const response = await referralCodeAPI.getMyReferralCode();
      if (response.data.has_assigned_code) setReferralCode(response.data.assigned_code);
    } catch (error) { console.error('Failed to load referral code:', error); } finally { setReferralCodeLoading(false); }
  };

  const handleEditProfile = () => {
    if (!canEditProfile) return;
    setEditMode(true);
    setEmailVerification({ otpSent: false, otp: '', verified: true, token: '', loading: false });
    setMobileVerification({ otpSent: false, otp: '', verified: true, token: '', loading: false });
    if (window.innerWidth <= 960 && profileInfoRef.current) {
      setTimeout(() => profileInfoRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setFieldErrors({});
    setError('');
    setSuccess('');
    setEmailVerification({ otpSent: false, otp: '', verified: false, token: '', loading: false });
    setMobileVerification({ otpSent: false, otp: '', verified: false, token: '', loading: false });
    loadProfile();
    if (window.innerWidth <= 960) setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    const errors = validateFields(profile);
    const currentEmail = (profile.email || '').trim().toLowerCase();
    const originalEmail = originalContactRef.current.email;
    const currentWhatsapp = (profile.whatsapp_number || '').trim();
    const currentWhatsappCode = (profile.whatsapp_code || '').trim();
    const originalWhatsapp = originalContactRef.current.whatsapp_number;
    const originalWhatsappCode = originalContactRef.current.whatsapp_code;
    const emailChanged = currentEmail !== originalEmail;
    const mobileChanged = currentWhatsapp !== originalWhatsapp || currentWhatsappCode !== originalWhatsappCode;

    if (emailChanged && !emailVerification.verified) {
      errors.email = 'Please verify updated email before saving.';
    }
    if (mobileChanged && !mobileVerification.verified) {
      errors.whatsapp_number = 'Please verify updated mobile number before saving.';
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) { setLoading(false); return; }
    try {
      // Only send whatsapp_number/code pair if a number is actually entered.
      // Backend returns 400 if whatsapp_code is sent without whatsapp_number (or vice versa).
      const hasWhatsapp = !!(profile.whatsapp_number || '').trim();
      const updateData = {
        name: profile.name,
        email: profile.email,
        whatsapp_number: hasWhatsapp ? profile.whatsapp_number : '',
        whatsapp_code: hasWhatsapp ? (profile.whatsapp_code || '+91') : '',
        company_name: profile.company_name,
        gst_number: profile.gst_number,
        address: profile.address,
        country_id: profile.country_id,
        state_id: profile.state_id,
        city_id: profile.city_id,
        pincode: profile.pincode,
        email_verification_token: emailChanged ? emailVerification.token : null,
        mobile_verification_token: mobileChanged ? mobileVerification.token : null
      };
      await merchantAPI.updateProfile(updateData);
      await loadProfile();
      setEditMode(false);
      setSuccess('Profile updated successfully!');
      refreshProfile?.();
    } catch (error) {
      console.error('Failed to update profile:', error);
      setError(error.response?.data?.error || 'Failed to update profile');
    } finally { setLoading(false); }
  };

  const sendEmailVerificationOtp = async () => {
    const emailStr = (profile.email || '').trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailStr)) {
      setFieldErrors(prev => ({ ...prev, email: 'Enter a valid email address.' }));
      return;
    }

    try {
      setEmailVerification(prev => ({ ...prev, loading: true }));
      await merchantAuthAPI.sendProfileEmailOtp(emailStr);
      setEmailVerification(prev => ({ ...prev, otpSent: true, verified: false, token: '' }));
      setEmailOtpArray(['', '', '', '']);
      setEmailOtpOpen(true);
      setSuccess('OTP sent to email');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to send email OTP');
    } finally {
      setEmailVerification(prev => ({ ...prev, loading: false }));
    }
  };

  const verifyEmailOtp = async () => {
    try {
      const otpStr = emailOtpArray.join('').replace(/\D/g, '');
      if (otpStr.length < 4) {
        setError('Please enter the complete 4-digit code');
        return;
      }
      setEmailVerification(prev => ({ ...prev, loading: true }));
      const response = await merchantAuthAPI.verifyProfileEmailOtp(
        (profile.email || '').trim().toLowerCase(),
        otpStr
      );
      setEmailVerification({ otpSent: true, otp: otpStr, verified: true, token: response.data.verification_token || '', loading: false });
      setFieldErrors(prev => ({ ...prev, email: '' }));
      setSuccess('Email verified successfully');
      setEmailOtpOpen(false);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to verify email OTP');
      setEmailVerification(prev => ({ ...prev, loading: false }));
    }
  };

  const sendMobileVerificationOtp = async () => {
    const mobileStr = (profile.whatsapp_number || '').trim();
    const countryCode = (profile.whatsapp_code || '+91').trim();

    if (!validatePhone(mobileStr, countryCode)) {
      setFieldErrors(prev => ({ ...prev, whatsapp_number: 'Please enter a valid whatsapp number.' }));
      return;
    }

    try {
      setMobileVerification(prev => ({ ...prev, loading: true }));
      await merchantAuthAPI.sendProfileMobileOtp(mobileStr, countryCode);
      setMobileVerification(prev => ({ ...prev, otpSent: true, verified: false, token: '' }));
      setMobileOtpArray(['', '', '', '']);
      setMobileOtpOpen(true);
      setSuccess('OTP sent to mobile');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to send mobile OTP');
    } finally {
      setMobileVerification(prev => ({ ...prev, loading: false }));
    }
  };

  const verifyMobileOtp = async () => {
    try {
      const otpStr = mobileOtpArray.join('').replace(/\D/g, '');
      if (otpStr.length < 4) {
        setError('Please enter the complete 4-digit code');
        return;
      }
      setMobileVerification(prev => ({ ...prev, loading: true }));
      const response = await merchantAuthAPI.verifyProfileMobileOtp(
        (profile.whatsapp_number || '').trim(),
        (profile.whatsapp_code || '+91').trim(),
        otpStr
      );
      setMobileVerification({ otpSent: true, otp: otpStr, verified: true, token: response.data.verification_token || '', loading: false });
      setFieldErrors(prev => ({ ...prev, whatsapp_number: '' }));
      setSuccess('Mobile verified successfully');
      setMobileOtpOpen(false);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to verify mobile OTP');
      setMobileVerification(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCountryChange = (e) => {
    const countryId = e.target.value;
    setProfile(prev => ({ ...prev, country_id: countryId, state_id: '', city_id: '' }));
    setStates([]); setCities([]);
    loadStates(countryId);
    setFieldErrors(prev => ({ ...prev, country_id: '', state_id: '', city_id: '' }));
  };

  // Country code dropdown handlers
  const handleCountryCodeClick = (event) => {
    setCountryAnchorEl(event.currentTarget);
    setCountrySearch('');
  };

  const handleCountryCodeClose = () => {
    setCountryAnchorEl(null);
    setCountrySearch('');
  };

  const handleCountryCodeSelect = (country) => {
    setProfile(prev => ({
      ...prev,
      whatsapp_code: country.country_code
    }));
    setFieldErrors(prev => ({ ...prev, whatsapp_code: '' }));
    const changed = (profile.whatsapp_number || '').trim() !== originalContactRef.current.whatsapp_number || (country.country_code || '').trim() !== originalContactRef.current.whatsapp_code;
    setMobileVerification({ otpSent: false, otp: '', verified: !changed, token: '', loading: false });
    handleCountryCodeClose();
  };

  const filteredCountryCodes = countryCodes.filter(c =>
    (c.country_name || '').toLowerCase().includes(countrySearch.toLowerCase()) ||
    (c.country_code || '').includes(countrySearch)
  );

  useEffect(() => {
    if (countryAnchorEl) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
        if (selectedCountryRef.current) {
          selectedCountryRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [countryAnchorEl]);

  const handleStateChange = (e) => {
    const stateId = e.target.value;
    setProfile(prev => ({ ...prev, state_id: stateId, city_id: '' }));
    setCities([]);
    loadCities(stateId);
    setFieldErrors(prev => ({ ...prev, state_id: '', city_id: '' }));
  };

  const handleCityChange = (e) => {
    const cityId = e.target.value;
    setProfile(prev => ({ ...prev, city_id: cityId }));
    setFieldErrors(prev => ({ ...prev, city_id: '' }));
  };

  const handleCopyReferralCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setCopySuccess(true);
    setCopyMessage('Full referral message copied to clipboard!');
    setTimeout(() => { setCopySuccess(false); setCopyMessage(''); }, 2000);
  };

  const handleReferralCodeShare = async () => {
    if (!referralCode) { setError('No referral code available to share'); return; }
    if (FlutterServicesAPI.isFlutterApp()) {
      try {
        const referralUrl = getRegisterUrl(referralCode);
        const shareData = { referralCode, shareMessage: `Hi! I'm using Glammify to create professional model photos of my jewellery instantly. You should try it too 👉 ${referralUrl}\nUse my code ${referralCode} for bonus ${FlutterServicesAPI.getCreditLabel().toLowerCase()}!`, shareUrl: referralUrl, title: 'Share Glammify Referral Code' };
        const result = await window.flutter_inappwebview.callHandler('socialShare', shareData);
        if (result && result.success) setSuccess('Referral code shared successfully!');
      } catch (error) { console.error('Error calling Flutter socialShare handler:', error); }
    } else {
      setShareDialog(true);
    }
  };

  const inputStyles = {
    '& .MuiInputBase-input': { fontSize: { xs: '14px', sm: '14px', md: '16px' } },
    '& .MuiInputLabel-root': { fontSize: { xs: '14px', sm: '14px', md: '16px' } }
  };
  const focusStyles = {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6318C6 !important', borderWidth: '2px !important' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#6318C6 !important' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6318C6' }
  };

  return (
    <Container className="mt-mb-0" maxWidth="lg" sx={{ mt: { xs: 0, sm: 0, md: 4 }, mb: { xs: 0, sm: 2, md: 4 }, px: { xs: 0, sm: 2, md: 3 } }}>
      <Paper elevation={0} sx={{ p: { xs: 0.5, sm: 2, md: 4 }, pt: { xs: 0, sm: 3, md: 3 }, boxShadow: 'none', border: { xs: 'none', sm: '1px solid #e5e7eb', md: '1px solid #e5e7eb' }, bgcolor: { xs: 'transparent', sm: 'white', md: 'white' }, borderRadius: '8px' }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} px={{ xs: 0.5, sm: 0 }}>
          <Button onClick={() => navigate('/dashboard')}
            startIcon={
              <svg xmlns="http://www.w3.org/2000/svg" width="8" height="13" viewBox="0 0 8 13" fill="none">
                <path d="M6.41309 0.916079L0.916291 6.41287L6.41309 11.9097" stroke="#651BC6" strokeWidth="1.83226" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
            sx={{ color: '#4A5565', px: 0, textTransform: 'none', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500, fontSize: '17.59px', '&:hover': { background: 'transparent', opacity: 0.8 } }}>
            Back
          </Button>
          <Typography variant="h6" sx={{ color: '#1E2939', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '17.59px' }}>My Profile</Typography>
        </Box>

        <AutoCloseAlert
          severity="error"
          message={error}
          show={!!error}
          onClose={() => setError('')}
          autoCloseDelay={4000}
        />
        <AutoCloseAlert severity="success" message={success} show={!!success} onClose={() => setSuccess('')} autoCloseDelay={4000} />
        <Grid container spacing={3} sx={{ px: { xs: 0.5, sm: 0 } }}>
          {/* Left Column - Avatar / Referral */}
          <Grid item xs={12} md={4} pt={0.5}>
            <Card elevation={0} sx={{
              boxShadow: '0px 0px 4.52px 0px rgba(0, 0, 0, 0.25)',
              border: 'none',
              borderRadius: '11.3px',
              overflow: 'hidden'
            }}>
              <Box sx={{
                height: '100px',
                background: 'linear-gradient(180deg, #6400CD 0%, #F59E0B 100%)',
                width: '100%',
                opacity: 0.65
              }} />
              <CardContent sx={{ textAlign: 'center', pt: 0, position: 'relative' }}>
                <Avatar sx={{
                  width: 100,
                  height: 100,
                  mx: 'auto',
                  mt: '-50px',
                  mb: 2,
                  background: '#e0e0e0',
                  color: '#000',
                  fontSize: '2rem',
                  border: '7px solid white',
                  position: 'relative',
                  zIndex: 2
                }}>
                  {profile.cover_image ? <img src={profile.cover_image} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : profile.name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '16.95px', color: '#000000', mb: 0.5, lineHeight: '1.2' }}>{profile.name}</Typography>
                <Typography sx={{ wordBreak: 'break-all', overflowWrap: 'break-word', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500, fontSize: '14.69px', color: '#888888', mb: 3 }}>{profile.email}</Typography>

                {/* Referral Code */}
                {referralCodeLoading ? (
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <CircularProgress size={24} sx={{ color: '#6318C6', mb: 1 }} />
                  </Box>
                ) : referralCode ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                      position: 'relative',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 3,
                      py: 1.9,
                      border: '1px solid #6318C6',
                      borderRadius: '15px'
                    }}>
                      <Box sx={{
                        position: 'absolute',
                        top: '-12px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#6318C6',
                        px: 1.5,
                        py: 0.2,
                        borderRadius: '10px',
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 600,
                        fontFamily: 'var(--font-montserrat-bold)',
                        whiteSpace: 'nowrap',
                        letterSpacing: '0.05em',
                        height: 22,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}>
                        REFERRAL CODE
                      </Box>
                      <Typography sx={{ fontFamily: 'var(--font-montserrat-bold)', fontWeight: 700, fontSize: '15px', color: '#000', mr: 2, mt: 0.5, letterSpacing: '0.05em' }}>{referralCode}</Typography>
                      <IconButton onClick={handleReferralCodeShare} sx={{ p: 0, mt: 0.5, color: '#6318C6' }} title="Share referral code">
                        <img src="/assets/img/website/icons/share-p.svg" alt="Share" style={{ width: '20px', height: '20px' }} />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500, fontSize: '14px', color: '#888888' }}>No referral code assigned</Typography>
                  </Box>
                )}

                <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={3}>
                  <Button
                    variant="contained"
                    startIcon={<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M12.9911 3.95435L6.21251 10.7329L2.82324 7.34362" stroke="white" stroke-width="2.25951" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>}
                    sx={{
                      background: profile.email_verified ? '#10B981' : '#F59E0B',
                      color: 'white',
                      textTransform: 'none',
                      fontFamily: 'var(--font-montserrat-bold)',
                      fontWeight: 600,
                      fontSize: '14px',
                      borderRadius: '25px',
                      boxShadow: 'none',
                      px: 3,
                      py: 1,
                      pointerEvents: 'none',
                      '&:hover': { background: profile.email_verified ? '#10B981' : '#F59E0B', boxShadow: 'none' }
                    }}
                  >
                    {profile.email_verified ? 'Verified' : 'Not Verified'}
                  </Button>

                  {canEditProfile && (
                    <Button
                      variant="outlined"
                      startIcon={<img src="/assets/img/website/icons/edit-icon.svg" alt="Edit" style={{ width: '16px', height: '16px' }} />}
                      onClick={handleEditProfile}
                      disabled={editMode}
                      sx={{
                        borderColor: '#6318C6',
                        color: '#6318C6',
                        textTransform: 'none',
                        fontFamily: 'var(--font-montserrat-bold)',
                        fontWeight: 600,
                        fontSize: '14px',
                        borderRadius: '25px',
                        borderWidth: '1.5px',
                        px: 4,
                        py: 1,
                        '&:hover': { borderColor: '#6318C6', borderWidth: '1.5px', background: 'rgba(99, 24, 198, 0.04)' }
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </Box>

                {profile.created_at && (
                  <Typography sx={{ fontFamily: 'var(--font-montserrat)', fontSize: '14px', color: '#6A7282', fontWeight: 400, pb: 1 }}>
                    Member since {new Date(profile.created_at).toLocaleDateString('en-GB')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Profile Form */}
          <Grid item xs={12} md={8}>
            <Card elevation={0} sx={{ boxShadow: 'none', border: { xs: 'none', sm: 'none', md: 'none' }, bgcolor: { xs: 'transparent', sm: 'transparent', md: 'transparent' }, borderRadius: '8px' }}>
              <CardContent sx={{ padding: { xs: 0, sm: 0, md: '0px' }, '&:last-child': { paddingBottom: { xs: 0, sm: 0, md: '16px' } } }}>
                <Box ref={profileInfoRef} mb={3} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '18px', color: '#1E2939' }}>Profile Information</Typography>

                </Box>

                {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography className="profile-input-label">Full Name *</Typography>
                    <TextField 
                      fullWidth 
                      required 
                      placeholder="Enter your full name" 
                      className="profile-form-control" 
                      value={profile.name || ''} 
                      onChange={(e) => { 
                        const val = e.target.value;
                        // Prevent typing non-alphabetic characters (except space)
                        if (val === '' || /^[A-Za-z\s]+$/.test(val)) {
                          setProfile(p => ({ ...p, name: val })); 
                          setFieldErrors(prev => ({ ...prev, name: '' })); 
                        }
                      }} 
                      disabled={!editMode} 
                      error={!!fieldErrors.name} 
                      helperText={fieldErrors.name} 
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography className="profile-input-label">Email Address *</Typography>
                    <TextField fullWidth type="email" placeholder="Enter your email" className="profile-form-control" value={profile.email || ''} onChange={(e) => {
                      const email = e.target.value;
                      setProfile(p => ({ ...p, email }));
                      setFieldErrors(prev => ({ ...prev, email: '' }));
                      const changed = email.trim().toLowerCase() !== originalContactRef.current.email;
                      setEmailVerification({ otpSent: false, otp: '', verified: !changed, token: '', loading: false });
                    }} disabled={!editMode} required error={!!fieldErrors.email} helperText={fieldErrors.email} />
                    {editMode && (profile.email || '').trim().toLowerCase() !== originalContactRef.current.email && (
                      <Box sx={{ mt: 1, display: 'flex', width: '100%' }}>
                        <Button
                          fullWidth
                          size="small"
                          sx={{
                            borderRadius: '10px',
                            backgroundColor: emailVerification.verified ? '#10B981' : '#651BC6',
                            color: '#fff',
                            border: `1px solid ${emailVerification.verified ? '#10B981' : '#651BC6'}`,
                            height: '42px',
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '14px',
                            fontFamily: 'var(--font-montserrat-bold, Montserrat, sans-serif)',
                            boxShadow: 'none',
                            cursor: emailVerification.verified ? 'default' : 'pointer',
                            '&:hover': {
                              backgroundColor: emailVerification.verified ? '#10B981' : '#5015a1',
                              border: `1px solid ${emailVerification.verified ? '#10B981' : '#5015a1'}`,
                              boxShadow: 'none',
                            },
                            '&:disabled': {
                              backgroundColor: '#d1d5db',
                              borderColor: '#d1d5db',
                              color: '#9ca3af',
                            },
                          }}
                          variant={emailVerification.verified ? "contained" : "outlined"} 
                          onClick={emailVerification.verified ? null : sendEmailVerificationOtp} 
                          disabled={emailVerification.loading || emailVerification.verified}>
                          {emailVerification.verified ? '✔ Verified' : 'Verify Email'}
                        </Button>
                      </Box>
                    )}
                  </Grid>



                  <Grid item xs={12} md={6}>
                    <Typography className="profile-input-label">
                      {showWhatsAppSmsAuth ? 'Whatsapp Number *' : 'Mobile Number'}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Box
                        sx={{
                          minWidth: 100,
                          height: 'var(--input-height-44)',
                          display: 'flex',
                          alignItems: 'center',
                          px: '12px',
                          border: '1px solid #C1C1C1',
                          borderRadius: '10px',
                          backgroundColor: '#f5f5f5',
                          cursor: 'default',
                          pointerEvents: 'none',
                          fontFamily: 'var(--font-montserrat-medium)',
                          fontSize: 'var(--font-size-small)',
                          color: '#1A1A1A',
                          justifyContent: 'space-between'
                        }}
                      >
                        <span>{profile.whatsapp_code || '+91'}</span>
                      </Box>

                      {/* Country Code Popover */}
                      <Popover
                        open={Boolean(countryAnchorEl)}
                        anchorEl={countryAnchorEl}
                        onClose={handleCountryCodeClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        PaperProps={{
                          sx: {
                            width: '300px',
                            maxHeight: '350px',
                            borderRadius: '12px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            mt: 0.5,
                          }
                        }}
                      >
                        {/* Search field */}
                        <Box sx={{ p: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                          <TextField
                            inputRef={searchInputRef}
                            fullWidth
                            size="small"
                            placeholder="Search country..."
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Search sx={{ fontSize: 18, color: '#9e9e9e' }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontFamily: 'var(--font-montserrat-medium)',
                              }
                            }}
                          />
                        </Box>
                        {/* Country list */}
                        <List sx={{ p: 0, overflow: 'auto', maxHeight: '280px' }}>
                          {filteredCountryCodes.map((country) => (
                            <ListItemButton
                              key={country.id}
                              ref={country.country_code === profile.whatsapp_code ? selectedCountryRef : null}
                              onClick={() => handleCountryCodeSelect(country)}
                              selected={country.country_code === profile.whatsapp_code}
                              sx={{
                                py: 0.8,
                                px: 2,
                                '&.Mui-selected': {
                                  backgroundColor: '#f3e8ff',
                                },
                                '&:hover': {
                                  backgroundColor: '#f9f5ff',
                                },
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Typography sx={{ fontSize: '13px', fontFamily: 'var(--font-montserrat-medium)', color: '#1A1A1A' }}>
                                    <span style={{ display: 'inline-block', width: '45px', textAlign: 'left' }}>{country.country_code}</span>
                                    {country.country_name}
                                  </Typography>
                                }
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      </Popover>
                      {fieldErrors.whatsapp_code && (<Typography variant="caption" color="error" sx={{ position: 'absolute', bottom: -20, left: 5 }}>{fieldErrors.whatsapp_code}</Typography>)}
                      <TextField fullWidth required={showWhatsAppSmsAuth} placeholder={showWhatsAppSmsAuth ? 'Whatsapp Number' : 'Mobile Number (optional)'} className="profile-form-control" type="tel" inputMode="tel" value={profile.whatsapp_number || ''} onChange={(e) => {
                        const whatsapp_number = e.target.value.replace(/\D/g, '').slice(0, 15);
                        setProfile(p => ({ ...p, whatsapp_number }));
                        setFieldErrors(prev => ({ ...prev, whatsapp_number: '' }));
                        const changed = whatsapp_number !== originalContactRef.current.whatsapp_number || (profile.whatsapp_code || '').trim() !== originalContactRef.current.whatsapp_code;
                        setMobileVerification({ otpSent: false, otp: '', verified: !changed, token: '', loading: false });
                      }} disabled error={!!fieldErrors.whatsapp_number} helperText={fieldErrors.whatsapp_number}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {showWhatsAppSmsAuth ? (
                                <WhatsApp sx={{ color: '#919191', fontSize: '22px' }} />
                              ) : (
                                <Person sx={{ color: '#919191', fontSize: '22px' }} />
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    {editMode && (((profile.whatsapp_number || '').trim() !== originalContactRef.current.whatsapp_number) || ((profile.whatsapp_code || '').trim() !== originalContactRef.current.whatsapp_code)) && (
                      <Box sx={{ mt: 1, display: 'flex', width: '100%' }}>
                        <Button
                          fullWidth
                          size="small"
                          sx={{
                            borderRadius: '10px',
                            backgroundColor: mobileVerification.verified ? '#10B981' : '#651BC6',
                            color: '#fff',
                            border: `1px solid ${mobileVerification.verified ? '#10B981' : '#651BC6'}`,
                            height: '42px',
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '14px',
                            fontFamily: 'var(--font-montserrat-bold, Montserrat, sans-serif)',
                            boxShadow: 'none',
                            cursor: mobileVerification.verified ? 'default' : 'pointer',
                            '&:hover': {
                              backgroundColor: mobileVerification.verified ? '#10B981' : '#5015a1',
                              border: `1px solid ${mobileVerification.verified ? '#10B981' : '#5015a1'}`,
                              boxShadow: 'none',
                            },
                            '&:disabled': {
                              backgroundColor: '#d1d5db',
                              borderColor: '#d1d5db',
                              color: '#9ca3af',
                            },
                          }}
                          variant={mobileVerification.verified ? "contained" : "outlined"} 
                          onClick={mobileVerification.verified ? null : sendMobileVerificationOtp} 
                          disabled={mobileVerification.loading || mobileVerification.verified}>
                          {mobileVerification.verified ? '✔ Verified' : 'Verify No'}
                        </Button>
                      </Box>
                    )}
                  </Grid>


                  <Grid item xs={12} md={6}>
                    <Typography className="profile-input-label">Company Name *</Typography>
                    <TextField fullWidth required placeholder="Datagrid Solutions" className="profile-form-control" value={profile.company_name || ''} onChange={(e) => { setProfile(p => ({ ...p, company_name: e.target.value })); setFieldErrors(prev => ({ ...prev, company_name: '' })); }} disabled={!editMode} error={!!fieldErrors.company_name} helperText={fieldErrors.company_name} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography className="profile-input-label">GST Number</Typography>
                    <TextField fullWidth placeholder="27AAPFU0939F1ZV" className="profile-form-control" value={profile.gst_number || ''} onChange={(e) => { setProfile(p => ({ ...p, gst_number: e.target.value })); setFieldErrors(prev => ({ ...prev, gst_number: '' })); }} disabled={!editMode} error={!!fieldErrors.gst_number} helperText={fieldErrors.gst_number || ' '} />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className="profile-input-label">Address</Typography>
                    <TextField fullWidth placeholder="Enter your full address" className="profile-form-control" multiline rows={3} value={profile.address || ''} onChange={(e) => { setProfile(p => ({ ...p, address: e.target.value })); setFieldErrors(prev => ({ ...prev, address: '' })); }} disabled={!editMode} error={!!fieldErrors.address} helperText={fieldErrors.address} sx={{ '& .MuiOutlinedInput-root': { height: 'auto !important', minHeight: '48px', alignItems: 'flex-start' } }} />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography className="profile-input-label">Country</Typography>
                    <FormControl fullWidth error={!!fieldErrors.country_id} disabled={!editMode} className="profile-form-control">
                      <Select displayEmpty value={profile.country_id || ''} onChange={handleCountryChange}>
                        <MenuItem value="" disabled>Select Country</MenuItem>
                        {countries.map((country) => (<MenuItem key={country.id} value={country.id}>{country.country_name}</MenuItem>))}
                      </Select>
                      {fieldErrors.country_id && (<Typography variant="caption" color="error">{fieldErrors.country_id}</Typography>)}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography className="profile-input-label">State</Typography>
                    <FormControl fullWidth error={!!fieldErrors.state_id} disabled={!editMode || loadingStates} className="profile-form-control">
                      <Select displayEmpty value={profile.state_id || ''} onChange={handleStateChange}>
                        <MenuItem value="" disabled>{loadingStates ? 'Loading...' : 'Select State'}</MenuItem>
                        {states.map((state) => (<MenuItem key={state.id} value={state.id}>{state.state_name}</MenuItem>))}
                      </Select>
                      {fieldErrors.state_id && (<Typography variant="caption" color="error">{fieldErrors.state_id}</Typography>)}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography className="profile-input-label">City</Typography>
                    <FormControl fullWidth error={!!fieldErrors.city_id} disabled={!editMode || loadingCities} className="profile-form-control">
                      <Select displayEmpty value={profile.city_id || ''} onChange={handleCityChange}>
                        <MenuItem value="" disabled>{loadingCities ? 'Loading...' : 'Select City'}</MenuItem>
                        {cities.map((city) => (<MenuItem key={city.id} value={city.id}>{city.city_name}</MenuItem>))}
                      </Select>
                      {fieldErrors.city_id && (<Typography variant="caption" color="error">{fieldErrors.city_id}</Typography>)}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography className="profile-input-label">Pincode</Typography>
                    <TextField fullWidth placeholder="400074" className="profile-form-control" value={profile.pincode || ''} onChange={(e) => { setProfile(p => ({ ...p, pincode: e.target.value })); setFieldErrors(prev => ({ ...prev, pincode: '' })); }} disabled={!editMode} error={!!fieldErrors.pincode} helperText={fieldErrors.pincode} />
                  </Grid>
                </Grid>

                {editMode && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                    <Button variant="contained" onClick={handleProfileUpdate} disabled={loading}
                      sx={{
                        background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)',
                        color: 'white',
                        textTransform: 'none',
                        fontFamily: 'var(--font-montserrat-semibold)',
                        fontWeight: 600,
                        fontSize: '16px',
                        boxShadow: 'none',
                        width: { xs: '100%', md: '200px' },
                        height: '48px',
                        borderRadius: '15.15px',
                        '&:hover': { background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)', opacity: 0.9, boxShadow: 'none' }
                      }}>
                      Save Changes
                    </Button>
                    <Button variant="outlined" onClick={handleCancel}
                      sx={{
                        backgroundColor: '#f3f4f6',
                        color: 'black',
                        borderColor: 'black',
                        textTransform: 'none',
                        fontFamily: 'var(--font-montserrat-semibold)',
                        fontWeight: 600,
                        fontSize: '16px',
                        boxShadow: 'none',
                        width: { xs: '100%', md: '200px' },
                        height: '48px',
                        borderRadius: '15.15px',
                        '&:hover': { borderColor: 'black', backgroundColor: '#f3f4f6' }
                      }}>
                      Cancel
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Share Referral Code Dialog */}
      <Dialog open={shareDialog} onClose={() => setShareDialog(false)} maxWidth="sm" fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '17.39px',
            width: { xs: '95%', sm: '480px' },
            maxWidth: '480px',
            m: 1
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #E5E7EB',
            px: 1.5,
            py: 1,
            pb: 1.5,
          }}
        >
          <Typography sx={{ fontSize: '17.39px', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, color: '#000000', lineHeight: 1.3 }}>Share Your Referral Code</Typography>
          <IconButton
            onClick={() => setShareDialog(false)}
            size="small"
            sx={{
              bgcolor: '#651BC6',
              color: 'white',
              width: 30,
              height: 30,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { bgcolor: '#5115a3' },
            }}
          >
            <img src="/assets/img/icons/close_icon.svg" alt="Close" style={{ width: 15, height: 15 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3, mt: 3 }}>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500, fontSize: '14px', color: '#6B7280', mb: 1 }}>Referral Code</Typography>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-bold)', fontWeight: 700, fontSize: { xs: '22px', sm: '26px' }, color: '#6318C6', letterSpacing: '0.15em', mb: 3 }}>{referralCode}</Typography>
            <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef', mb: 3, textAlign: 'left', wordBreak: 'break-all' }}>
              <Typography sx={{ lineHeight: 1.6, fontFamily: 'var(--font-montserrat-medium)', fontSize: '14px', color: '#374151', whiteSpace: 'pre-line', wordBreak: 'break-all' }}>
                {`Hi! I'm using Glammify to create professional model photos of my jewellery instantly. You should try it too 👉 ${getRegisterUrl(referralCode)}\nUse my code ${referralCode} for bonus ${FlutterServicesAPI.getCreditLabel().toLowerCase()}!`}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2, width: '100%', maxWidth: { sm: '320px' }, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', mx: 'auto' }}>
            <Button
              className="btn-primary p-12-0 font-14 ms-0"
              startIcon={<ContentCopy style={{ width: 18, height: 18 }} />}
              onClick={() => { const fullMessage = `Hi! I'm using Glammify to create professional model photos of my jewellery instantly. You should try it too 👉 ${getRegisterUrl(referralCode)}\nUse my code ${referralCode} for bonus ${FlutterServicesAPI.getCreditLabel().toLowerCase()}!`; handleCopyReferralCode(fullMessage); setShareDialog(false); }}
              sx={{
                flex: 1,
                height: '42px !important',
                borderRadius: '10px !important',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                justifyContent: 'center',
                textTransform: 'none',
                fontFamily: 'var(--font-montserrat-medium)',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Copy Message
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar open={copySuccess} autoHideDuration={3000} onClose={() => setCopySuccess(false)} message={copyMessage} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ '& .MuiSnackbarContent-root': { backgroundColor: '#4caf50', color: 'white', fontWeight: 500 } }}
      />

      {/* Email OTP Dialog */}
      <Dialog open={emailOtpOpen} onClose={() => setEmailOtpOpen(false)} maxWidth="sm" fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '18px', p: 1, width: {
              xs: '90%',   // mobile
              sm: '400px', // tablet
              md: '420px', // desktop
            },
          }
        }}>
        <DialogTitle sx={{ px: 2, pt: 3, pb: 1, borderBottom: 'none', textAlign: 'center', position: 'relative' }}>
          <Typography variant="h6" className="font-montserret-semibold" sx={{ fontWeight: 600, fontSize: '18px', color: '#1A1A1A' }}>Verify Email</Typography>
          <IconButton onClick={() => setEmailOtpOpen(false)} sx={{ position: 'absolute', right: 8, top: 12, color: '#6b7280' }}>
            <Cancel />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="body2" className="font-roboto-condensed" sx={{ mb: 3, textAlign: 'center', color: '#27252E', fontSize: '16px', lineHeight: 1.5 }}>
            We just sent a 4-digit code to <strong>{profile.email}</strong>, enter it below:
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1, sm: 2 }, mb: 3, width: '100%' }}>
            {emailOtpArray.map((digit, index) => (
              <TextField
                key={index}
                inputRef={emailOtpRefs[index]}
                value={digit}
                onChange={(e) => handleOtpChange('email', index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown('email', index, e)}
                onPaste={index === 0 ? (e) => handlePaste('email', e) : undefined}
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                inputProps={{
                  maxLength: index === 0 ? 4 : 1,
                  inputMode: 'numeric',
                  style: { textAlign: 'center', fontSize: '24px', padding: '12px 0px', fontWeight: 500 }
                }}
                sx={{
                  flex: 1,
                  maxWidth: '82px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    '& fieldset': { borderColor: '#BDBDBD' },
                    '&.Mui-focused fieldset': { borderColor: '#A864ED' },
                    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(168, 100, 237, 0.2)' }
                  }
                }}
              />
            ))}
          </Box>
          <Button
            fullWidth
            variant="contained"
            onClick={verifyEmailOtp}
            disabled={emailVerification.loading || emailOtpArray.join('').replace(/\D/g, '').length < 4}
            sx={{
              background: 'linear-gradient(90deg, #A864ED 0%, #F4B876 100%)',
              borderRadius: '10px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 600,
              py: 1.5,
              mb: 1,
              boxShadow: 'none',
              '&:hover': {
                background: 'linear-gradient(90deg, #9b5ce0 0%, #e8ad6b 100%)',
                boxShadow: 'none',
              }
            }}
          >
            {emailVerification.loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Mobile OTP Dialog */}
      <Dialog open={mobileOtpOpen} onClose={() => setMobileOtpOpen(false)} maxWidth="sm" fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '18px', p: 1, width: {
              xs: '90%',   // mobile
              sm: '400px', // tablet
              md: '420px', // desktop
            },
          }
        }}>
        <DialogTitle sx={{ px: 2, pt: 3, pb: 1, borderBottom: 'none', textAlign: 'center', position: 'relative' }}>
          <Typography variant="h6" className="font-montserret-semibold" sx={{ fontWeight: 600, fontSize: '18px', color: '#1A1A1A' }}>Verify Number</Typography>
          <IconButton onClick={() => setMobileOtpOpen(false)} sx={{ position: 'absolute', right: 8, top: 12, color: '#6b7280' }}>
            <Cancel />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="body2" className="font-roboto-condensed" sx={{ mb: 3, textAlign: 'center', color: '#27252E', fontSize: '16px', lineHeight: 1.5 }}>
            We just sent a 4-digit code to <strong>{(profile.whatsapp_code || '+91').trim()} {(profile.whatsapp_number || '').trim()}</strong>, enter it below:
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1, sm: 2 }, mb: 3, width: '100%' }}>
            {mobileOtpArray.map((digit, index) => (
              <TextField
                key={index}
                inputRef={mobileOtpRefs[index]}
                value={digit}
                onChange={(e) => handleOtpChange('mobile', index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown('mobile', index, e)}
                onPaste={index === 0 ? (e) => handlePaste('mobile', e) : undefined}
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                inputProps={{
                  maxLength: index === 0 ? 4 : 1,
                  inputMode: 'numeric',
                  style: { textAlign: 'center', fontSize: '24px', padding: '12px 0px', fontWeight: 500 }
                }}
                sx={{
                  flex: 1,
                  maxWidth: '82px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    '& fieldset': { borderColor: '#BDBDBD' },
                    '&.Mui-focused fieldset': { borderColor: '#A864ED' },
                    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(168, 100, 237, 0.2)' }
                  }
                }}
              />
            ))}
          </Box>
          <Button
            fullWidth
            variant="contained"
            onClick={verifyMobileOtp}
            disabled={mobileVerification.loading || mobileOtpArray.join('').replace(/\D/g, '').length < 4}
            sx={{
              background: 'linear-gradient(90deg, #A864ED 0%, #F4B876 100%)',
              borderRadius: '10px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 600,
              py: 1.5,
              mb: 1,
              boxShadow: 'none',
              '&:hover': {
                background: 'linear-gradient(90deg, #9b5ce0 0%, #e8ad6b 100%)',
                boxShadow: 'none',
              }
            }}
          >
            {mobileVerification.loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
          </Button>
        </DialogContent>
      </Dialog>
    </Container >
  );
};

export default ProfilePage;
